import { motion } from 'framer-motion';
import {
  Award,
  Globe,
  Heart,
  Shield,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const About = () => {
  const aboutStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Documint',
    url: 'https://documint.id/about',
    mainEntity: {
      '@type': 'Organization',
      name: 'Documint',
      alternateName: 'Documint ID',
      url: 'https://documint.id',
      description:
        'Documint is a digital trust platform that enables organizations to issue, manage, and verify tamper-proof digital credentials and certificates built on W3C Verifiable Credentials and decentralized identifiers.',
      foundingDate: '2023',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bangalore',
        addressRegion: 'Karnataka',
        addressCountry: 'IN',
      },
    },
  };

  const values = [
    {
      icon: Heart,
      title: 'Customer First',
      description:
        'We put our customers at the center of everything we do, ensuring their success is our success.',
    },
    {
      icon: Shield,
      title: 'Security & Trust',
      description:
        'We maintain the highest standards of security and privacy to earn and keep your trust.',
    },
    {
      icon: Zap,
      title: 'Innovation',
      description:
        'We continuously innovate to provide cutting-edge solutions that solve real business problems.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      description:
        'We believe in the power of teamwork and foster a culture of collaboration and mutual support.',
    },
  ];

  const team = [
    {
      name: 'Kodiswaran Babu Janardhanan',
      role: 'CEO & Co-founder',
      bio: 'Ex-Qualcomm, Motorola, Fidelity Investments. Visionary leader with expertise in decentralized identity and blockchain technology.',
      image: '/assets/images/Kodis.jpg',
      type: 'leadership',
    },
  ];

  const advisors = [
    {
      name: 'Team from Fortune 500',
      role: 'Engineering & Product',
      bio: 'Experienced professionals from leading technology companies bringing enterprise-scale expertise.',
      image: '/assets/images/team-placeholder.jpg',
      type: 'team',
    },
    {
      name: 'Industry Advisors',
      role: 'Strategic Guidance',
      bio: 'Highly skilled advisors from reputed companies providing strategic direction and industry insights.',
      image: '/assets/images/advisors-placeholder.jpg',
      type: 'advisors',
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEOHead
        title="About Documint"
        description="Documint is a digital trust platform that enables organizations to issue, manage, and verify tamper-proof digital credentials built on W3C Verifiable Credentials and decentralized identifiers."
        url="https://documint.id/about"
        structuredData={aboutStructuredData}
      />
      {/* Hero Section */}
      <section className="bg-surface-100 py-20">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              About
              <span className="text-neon-300"> Documint</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Documint is a digital trust platform that enables organizations to
              issue, manage, and verify tamper-proof digital credentials. We are
              building an open, developer‑first platform for Self‑Sovereign
              Identity: decentralized identifiers, verifiable credentials, and
              interoperability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-surface-200/40">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-300 mb-6">
                Identity should be user‑owned and privacy‑preserving. We provide
                simple building blocks for developers to issue, hold, and verify
                credentials anywhere.
              </p>
              <p className="text-lg text-gray-300 mb-8">
                We are committed to open standards, great DX, and
                enterprise‑grade security so you can ship trusted identity
                experiences fast.
              </p>
              <div className="flex items-center space-x-4">
                <Target className="w-8 h-8 text-neon-300" />
                <span className="text-lg font-semibold text-white">
                  Empowering developers to build trusted identity
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Developer-First
                    </h3>
                    <p className="text-gray-600">
                      Open APIs and SDKs for easy integration
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Cutting-Edge Tech
                    </h3>
                    <p className="text-gray-600">
                      Built on W3C standards and blockchain
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Early Stage Growth
                    </h3>
                    <p className="text-gray-600">
                      Growing pilot partnerships and community
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-surface-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              These principles guide our work: open standards, privacy by
              design, and developer empathy.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl p-6 text-center transition-all duration-300 hover:scale-105"
                style={{
                  background:
                    index === 0
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : index === 1
                      ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                      : index === 2
                      ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                      : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                }}
              >
                {/* Animated background overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Floating particles effect */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-pulse"></div>
                  <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-white/30 rounded-full animate-bounce"></div>
                  <div className="absolute bottom-1/4 left-3/4 w-1.5 h-1.5 bg-white/25 rounded-full animate-ping"></div>
                </div>

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 drop-shadow-sm">
                    {value.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </div>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-surface-200/40">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Meet Our Leadership
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our experienced team is passionate about building the future of
              digital identity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center max-w-6xl mx-auto">
            {[...team, ...advisors].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="group relative max-w-md overflow-hidden rounded-2xl p-8 text-center transition-all duration-500 hover:scale-105"
                style={{
                  background:
                    member.type === 'leadership'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                      : member.type === 'team'
                      ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                      : 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
                }}
              >
                {/* Animated background effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-neon-400/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700"></div>

                {/* Floating particles */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/30 rounded-full animate-pulse"></div>
                  <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-white/40 rounded-full animate-bounce delay-300"></div>
                  <div className="absolute bottom-1/3 left-2/3 w-1.5 h-1.5 bg-white/25 rounded-full animate-ping delay-700"></div>
                </div>

                <div className="relative z-10">
                  {/* Profile Image */}
                  <div className="relative mb-6">
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-white/30 shadow-2xl group-hover:border-white/50 transition-colors duration-300">
                      {member.type === 'leadership' ? (
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full bg-white/20 backdrop-blur-sm rounded-full ${
                          member.type === 'leadership' ? 'hidden' : 'flex'
                        } items-center justify-center`}
                      >
                        <Users className="w-16 h-16 text-white/80" />
                      </div>
                    </div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary-400/50 to-neon-400/50 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
                    {member.name}
                  </h3>
                  <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                    <p className="text-white font-semibold text-sm">
                      {member.role}
                    </p>
                  </div>
                  <p className="text-white/90 leading-relaxed text-sm max-w-sm mx-auto">
                    {member.bio}
                  </p>
                </div>

                {/* Bottom accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-neon-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Join Us in Building Open Identity
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Ready to bring SSI to your product? Get started with our sandbox
              and docs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                Get API Key
              </button>
              <button className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-primary-600 transition-colors duration-200">
                View Docs
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
